import React, { useEffect, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { Cue } from "../../shared/interfaces/GameLogicTypes";
import { renderCueContent } from "../../shared/helper/renderCueContent";
import "./HerdReveal.css";

export interface HerdRevealProps {
  options: Cue[];
  optionCounts: number[];
  playerChoice: number | null;
  isAlive: boolean;
  wasSplit: boolean;
  eliminations: string[];
  playerId: string;
  onAnimationComplete: () => void;
}

const HERD_COLORS = ["#608ad6", "#e75151", "#e8a838", "#7dc462"];

export default function HerdReveal({
  options,
  optionCounts,
  playerChoice,
  isAlive,
  wasSplit,
  eliminations,
  playerId,
  onAnimationComplete,
}: HerdRevealProps) {
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    const flipTimer = setTimeout(() => setFlipped(true), 300);
    const completeTimer = setTimeout(() => onAnimationComplete(), 3000);
    return () => {
      clearTimeout(flipTimer);
      clearTimeout(completeTimer);
    };
  }, []);

  const wasEliminated = eliminations.includes(playerId);

  // Find the majority side (0-indexed) — the option with the most votes
  let majoritySide = -1;
  if (!wasSplit) {
    const maxVotes = Math.max(...optionCounts);
    const tiedAtMax = optionCounts.filter((c) => c === maxVotes).length;
    if (tiedAtMax === 1) {
      majoritySide = optionCounts.indexOf(maxVotes);
    }
  }

  const playerSurvived = playerChoice != null && !wasEliminated && isAlive;

  const gridClass =
    options.length <= 2
      ? "herd-reveal-grid herd-reveal-grid-2"
      : options.length === 3
      ? "herd-reveal-grid herd-reveal-grid-3"
      : "herd-reveal-grid herd-reveal-grid-4";

  return (
    <Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
      <div className={gridClass}>
        {options.map((cue, idx) => {
          const isMajority = idx === majoritySide;
          const isMinority = !wasSplit && majoritySide !== -1 && idx !== majoritySide;
          const wasPlayerChoice = playerChoice === idx + 1;
          const backClass = wasSplit ? "split" : isMajority ? "majority" : "minority";

          return (
            <div key={idx} className="herd-card-flip">
              <div className={`herd-card-flip-inner${flipped ? " flipped" : ""}`}>
                {/* Front: the option */}
                <div
                  className="herd-card-front"
                  style={{
                    backgroundColor: HERD_COLORS[idx % HERD_COLORS.length],
                    border: wasPlayerChoice ? "3px solid #ffffff" : "3px solid transparent",
                    boxShadow: wasPlayerChoice ? "0 0 12px rgba(255,255,255,0.5)" : "none",
                  }}
                >
                  {typeof cue.content === "string" ? renderCueContent(cue.content) : ""}
                </div>
                {/* Back: vote count */}
                <div className={`herd-card-back ${backClass}`}>
                  <div className="herd-vote-count">{optionCounts[idx] ?? 0}</div>
                  <div className="herd-vote-label">votes</div>
                  {/* "one of us!" flying text on majority card when player survived */}
                  {isMajority && playerSurvived && wasPlayerChoice && (
                    <div className="herd-one-of-us-container">
                      <span className="herd-one-of-us herd-one-of-us-1">one of us!</span>
                      <span className="herd-one-of-us herd-one-of-us-2">one of us!</span>
                      <span className="herd-one-of-us herd-one-of-us-3">one of us!</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

    </Stack>
  );
}
